/* Lumetier · Industries preview — dark SaaS theme.
   Interactive visualization showing how Lumetier adapts to five service-business verticals. */

import { useState } from "react";
import {
  Camera, Scissors, GraduationCap, Flower2, Dumbbell, Calendar, FileSignature,
  CreditCard, ImagePlus, Star, ArrowRight, MessageSquare,
} from "lucide-react";
import LumetierLogo from "@/components/LumetierLogo";

const GOLD = "#C9A961";
const GOLD_LIGHT = "#EDD78C";

const INDUSTRIES = [
  {
    id: "photography",
    label: "Photography",
    icon: Camera,
    accent: "#C9A961",
    hero: "Run your photography studio with the grace of a boutique.",
    audience: "Wedding · Family · Newborn · Branding photographers",
    deliverable: "Client gallery + signed contract + invoice",
    sampleBooking: {
      service: "Family Session · 90 min",
      client: "Mary & Tom Adams",
      total: 750,
      deposit: 225,
      delivery: "Online gallery within 21 days · 40 edited photos",
    },
    features: [
      { icon: ImagePlus, title: "Client galleries", body: "Proofing, favorites, downloads — all branded to your studio." },
      { icon: FileSignature, title: "Contracts & releases", body: "Editable templates with e-signature for every session." },
      { icon: Star, title: "Auto-review requests", body: "Send a one-tap review prompt the day after delivery." },
    ],
  },
  {
    id: "beauty",
    label: "Beauty & Salon",
    icon: Scissors,
    accent: "#D679A1",
    hero: "Bookings, color formulas, and client memory — finally in one place.",
    audience: "Hairstylists · Colorists · Nail artists · Makeup artists",
    deliverable: "Appointment + color formula + before/after gallery",
    sampleBooking: {
      service: "Balayage + Toner · 3.5 hrs",
      client: "Olivia Reyes",
      total: 285,
      deposit: 50,
      delivery: "Color formula saved · Touch-up reminder in 8 weeks",
    },
    features: [
      { icon: MessageSquare, title: "Client memory", body: "Remember every formula, allergy, and preference — automatically." },
      { icon: ImagePlus, title: "Before / after gallery", body: "Build a portfolio that converts new clients on Instagram." },
      { icon: Calendar, title: "Smart reminders", body: "Touch-up reminders that match each client's natural cycle." },
    ],
  },
  {
    id: "tutoring",
    label: "Tutoring & Coaching",
    icon: GraduationCap,
    accent: "#7DA9C9",
    hero: "Spend more time teaching. Less time scheduling and chasing payments.",
    audience: "Academic tutors · Music teachers · Life & business coaches",
    deliverable: "Session + progress notes + payment record",
    sampleBooking: {
      service: "SAT Math Tutoring · 60 min",
      client: "James Chen (parent: Linda Chen)",
      total: 95,
      deposit: 0,
      delivery: "Progress notes shared with parent · Next session auto-scheduled",
    },
    features: [
      { icon: Calendar, title: "Recurring sessions", body: "Set a weekly cadence once. Rescheduling is a single tap." },
      { icon: MessageSquare, title: "Progress notes", body: "Private notes per session · Shareable parent summaries." },
      { icon: CreditCard, title: "Packages & prepay", body: "Sell 10-session packs with auto-debit. No more chasing checks." },
    ],
  },
  {
    id: "florist",
    label: "Florist & Event",
    icon: Flower2,
    accent: "#9AAE8F",
    hero: "From first consultation to wedding-day delivery, beautifully managed.",
    audience: "Wedding florists · Event designers · Boutique studios",
    deliverable: "Consultation + design proposal + delivery schedule",
    sampleBooking: {
      service: "Wedding Florals · Aug 14",
      client: "Sarah & Marcus · Vineyard Vows",
      total: 4250,
      deposit: 1500,
      delivery: "Mood board + proposal PDF · Delivery 8am Aug 14",
    },
    features: [
      { icon: ImagePlus, title: "Mood boards & proposals", body: "Visual design proposals clients can comment on directly." },
      { icon: FileSignature, title: "Multi-stage contracts", body: "Discovery → proposal → confirmation, with deposits at each step." },
      { icon: Calendar, title: "Delivery logistics", body: "Day-of schedule with venue contacts, route, and team assignments." },
    ],
  },
  {
    id: "fitness",
    label: "Trainer & Wellness",
    icon: Dumbbell,
    accent: "#E08960",
    hero: "Sessions, programs, and progress — for trainers who don't do spreadsheets.",
    audience: "Personal trainers · Yoga instructors · Wellness coaches",
    deliverable: "Session + program plan + check-in tracking",
    sampleBooking: {
      service: "1:1 Strength Training · 45 min",
      client: "David Okafor",
      total: 85,
      deposit: 0,
      delivery: "Workout logged · Next check-in Thursday",
    },
    features: [
      { icon: Calendar, title: "Auto-billed packages", body: "10-session packs with auto-renew · One less monthly admin task." },
      { icon: MessageSquare, title: "Client check-ins", body: "Lightweight weekly check-in form with mood + energy tracking." },
      { icon: Star, title: "Client retention alerts", body: "Get pinged when a regular hasn't booked in 2 weeks." },
    ],
  },
];

export default function LumetierIndustries() {
  const [selected, setSelected] = useState(INDUSTRIES[0].id);
  const ind = INDUSTRIES.find((i) => i.id === selected);

  return (
    <div
      className="min-h-screen bg-[#070709] text-stone-100 relative overflow-hidden"
      style={{ fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif" }}
      data-testid="lumetier-industries"
    >
      {/* Background accent glow that follows the selected industry */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[600px] transition-all duration-700"
          style={{ background: `radial-gradient(ellipse, ${ind.accent}22 0%, transparent 65%)` }}
        />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.022) 1px, transparent 1px), linear-gradient(to right, rgba(255,255,255,0.022) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
      </div>

      {/* Top brand bar */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 pt-10 pb-6 flex items-center justify-between border-b border-white/[0.07]">
        <a
          href="/lumetier-preview"
          className="text-[11px] uppercase tracking-[0.32em] text-stone-500 hover:text-stone-100 transition-colors"
        >
          ← Brand
        </a>
        <div className="text-[11px] uppercase tracking-[0.32em] text-stone-500 hidden sm:block">
          One platform · Five industries · Endless craft
        </div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="text-[11px] uppercase tracking-[0.32em] text-stone-500 mb-5">
            What Lumetier looks like for
          </div>
          <h1
            className="text-5xl md:text-7xl font-light leading-[0.95]"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            data-testid="industries-headline"
          >
            <em
              className="italic not-italic"
              style={{
                backgroundImage: `linear-gradient(135deg, ${ind.accent}, ${GOLD_LIGHT}, ${ind.accent})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                fontStyle: "italic",
                transition: "background-image .5s ease",
              }}
            >
              {ind.label}
            </em>
          </h1>
          <div
            className="h-px mx-auto mt-7 mb-6 transition-colors"
            style={{ background: ind.accent, width: 72 }}
          />
          <p className="max-w-2xl mx-auto text-stone-300 text-lg leading-relaxed">{ind.hero}</p>
          <p className="text-xs uppercase tracking-[0.22em] text-stone-500 mt-4">{ind.audience}</p>
        </div>

        {/* Industry switcher */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-16" data-testid="industry-switcher">
          {INDUSTRIES.map((i) => {
            const active = i.id === selected;
            const Icon = i.icon;
            return (
              <button
                key={i.id}
                onClick={() => setSelected(i.id)}
                data-testid={`tab-${i.id}`}
                className="inline-flex items-center gap-2 px-4 h-11 text-xs uppercase tracking-[0.18em] border transition-all"
                style={{
                  background: active ? i.accent : "rgba(255,255,255,0.02)",
                  color: active ? "#0d0d10" : "#a8a29e",
                  borderColor: active ? i.accent : "rgba(255,255,255,0.12)",
                  fontWeight: active ? 500 : 400,
                }}
              >
                <Icon className="w-4 h-4" />
                {i.label}
              </button>
            );
          })}
        </div>

        {/* Feature cards */}
        <div className="grid md:grid-cols-3 gap-3 mb-16">
          {ind.features.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                className="bg-white/[0.03] border border-white/[0.08] p-7 hover:bg-white/[0.06] hover:border-white/[0.16] transition-all"
              >
                <div
                  className="w-11 h-11 flex items-center justify-center mb-5"
                  style={{ background: `${ind.accent}1a`, color: ind.accent }}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <h3
                  className="text-2xl mb-2 font-light text-stone-100"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                >
                  {f.title}
                </h3>
                <p className="text-sm text-stone-400 leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>

        {/* Sample booking */}
        <div className="mb-16">
          <div className="text-[11px] uppercase tracking-[0.32em] text-stone-500 text-center mb-6">
            ✦ Sample booking on Lumetier ✦
          </div>
          <div
            className="max-w-xl mx-auto bg-[#0D0F14] border border-white/[0.1] p-6"
            data-testid="sample-booking"
          >
            <div className="flex items-start justify-between gap-4 pb-4 border-b border-white/[0.08]">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-1">Upcoming</div>
                <h4
                  className="text-2xl font-light text-stone-100"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                >
                  {ind.sampleBooking.service}
                </h4>
                <p className="text-sm text-stone-400 mt-1">{ind.sampleBooking.client}</p>
              </div>
              <span
                className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] shrink-0"
                style={{ background: ind.accent, color: "#0d0d10", fontWeight: 500 }}
              >
                Confirmed
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-1">Total</div>
                <div className="font-medium text-stone-100">${ind.sampleBooking.total.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-1">Deposit paid</div>
                <div className="font-medium text-stone-100">
                  {ind.sampleBooking.deposit > 0
                    ? `$${ind.sampleBooking.deposit.toLocaleString()}`
                    : "—"}
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/[0.08] text-sm text-stone-400">
              <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-1">Deliverable</div>
              {ind.sampleBooking.delivery}
            </div>
          </div>
        </div>

        {/* Bridge to the brand mark */}
        <div className="text-center mt-20 mb-12">
          <LumetierLogo size={180} inverted accent={GOLD} />
          <p className="text-stone-400 text-sm mt-10 max-w-md mx-auto leading-relaxed">
            One operating system. Pick your industry at signup — Lumetier takes care of the rest.
          </p>
          <a
            href="/lumetier-preview"
            className="inline-flex items-center gap-2 mt-8 px-6 h-12 text-xs uppercase tracking-[0.22em] text-stone-900 font-medium hover:opacity-90 transition-opacity"
            style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})` }}
          >
            Brand assets · Logo · Video
            <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
}
